import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {cloneDeep, isArray} from 'lodash';
import axios from 'axios';
import BudgetFields from './BudgetFields';
import DropdownFormGroup from '../form-components/DropdownFormGroup';
import TextareaFormGroup from '../form-components/TextareaFormGroup';
import DatepickerFormGroup from '../form-components/DatepickerFormGroup';
import ReadonlyFormGroup from '../form-components/ReadonlyFormGroup';
import ErrorMessageContainer from '../ErrorMessageContainer';
import Utils from '../Utils';
import {commaDecimalToFloat, floatToCommaDecimal} from '../utils/number';
import {fromSystemDate, toHumanDate, toSystemDate} from '../utils/date';
import {isNullOrUndefined} from '../utils/general';
import TextFormGroup from '../form-components/TextFormGroup';

const {amendmentDraft, paymentTerms, reviewers} = window;

const parseModel = (model) => {
    const budget = parseFloat(model.budget);
    const vat = floatToCommaDecimal(parseFloat(model.vat)) || '';

    const paymentTerm = paymentTerms.find((item) => item.value === model.payment_term) || null;

    return {
        budget: floatToCommaDecimal(budget) || '',
        vat: vat,
        budget_vat: Utils.calculateAmountVATFromAmount(vat, budget),
        contract_date: fromSystemDate(model.contract_date),
        start_date: toHumanDate(fromSystemDate(model.start_date)),
        end_date: fromSystemDate(model.end_date),
        payment_term: paymentTerm,
        notes: model.notes,
        hospital_protocol_number: model.hospital_protocol_number,
        reviewer_ids: window.selectedReviewers || [],
    };
}

const preparePayload = (model) => {
    const formData = new FormData();

    const budget = commaDecimalToFloat(model.budget);
    const vat = commaDecimalToFloat(model.vat);
    const contractDate = toSystemDate(model.contract_date);
    const endDate = toSystemDate(model.end_date);
    const paymentTerm = model.payment_term
        ? model.payment_term.value
        : null;
    const notes = model.notes;
    const hospitalProtocolNumber = model.hospital_protocol_number;

    formData.append(
        'budget',
        isNullOrUndefined(budget) ? '' : budget
    );
    formData.append(
        'vat',
        isNullOrUndefined(vat) ? '' : vat
    );
    formData.append(
        'contract_date',
        isNullOrUndefined(contractDate) ? '' : contractDate
    );
    formData.append(
        'end_date',
        isNullOrUndefined(endDate) ? '' : endDate
    );
    formData.append(
        'payment_term',
        isNullOrUndefined(paymentTerm) ? '' : paymentTerm
    );
    formData.append(
        'notes',
        isNullOrUndefined(notes) ? '' : notes
    );
    formData.append(
        'hospital_protocol_number',
        isNullOrUndefined(hospitalProtocolNumber) ? '' : hospitalProtocolNumber
    );

    const reviewerIds = model.reviewer_ids;

    if (isArray(reviewerIds)) {
        reviewerIds
            .map((item) => parseInt(item.value))
            .filter((item) => !isNaN(item) && item > 0)
            .map((item) => item + '')
            .forEach((item) => formData.append('reviewer_ids[]', item));
    }

    return formData;
}

export default class CreateAmendmentForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            model: {},
            isLoading: false,
            errors: [],
        };

        this.onSaveButtonClick = this.onSaveButtonClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onModelUpdate = this.onModelUpdate.bind(this);
    }

    componentWillMount() {
        this.setState({
            model: parseModel(amendmentDraft),
        });
    }

    onSaveButtonClick(e) {
        e.preventDefault();

        const payload = preparePayload(this.state.model);

        let endpoint = this.props.submitUrl;

        if (!endpoint) {
            endpoint = `/contracts/manage_contracts/amendment-drafts/${amendmentDraft.id}/edit`;
        }

        this.setState({
            isLoading: true,
            errors: [],
        }, () => {
            axios
                .post(endpoint, payload, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .then((response) => {
                    window.location.href = response.data.redirect_url;
                })
                .catch((error) => {
                    this.setState({
                        isLoading: false,
                        errors: error.response.data,
                    });
                });
        });
    }

    onChange(key, value) {
        const newModel = cloneDeep(this.state.model);
        newModel[key] = value;
        this.onModelUpdate(newModel);
    }

    onModelUpdate(newModel) {
        this.setState((prevState) => {
            return {
                model: {
                    ...prevState.model,
                    ...newModel,
                },
            };
        });
    }

    render() {
        return (
            <form method="post" className="form-horizontal">
                <div className="row">
                    <div className="col-md-12">
                        <div className="text-right">
                            {
                                this.state.isLoading
                                    ? <span style={{marginRight: '10px'}}>
                                        <i className="fa fa-fw fa-spin fa-spinner"></i> {window.__js_general.please_wait}
                                    </span>
                                    : null
                            }
                            <button className="btn btn-primary" onClick={this.onSaveButtonClick}
                                    disabled={this.state.isLoading}>
                                <i className="fa fa-fw fa-save"></i> {window.__js_general.save}
                            </button>
                        </div>
                    </div>
                </div>

                <br/>

                <ErrorMessageContainer errors={this.state.errors}/>

                <BudgetFields
                    budget={this.state.model.budget}
                    vat={this.state.model.vat}
                    budgetVat={this.state.model.budget_vat}
                    onStateUpdate={(data) => this.onModelUpdate(data)}
                />

                <DatepickerFormGroup
                    label={window.__js_contracts.contract_date}
                    name="contract_date_formatted"
                    value={this.state.model.contract_date}
                    onChange={(value) => this.onChange('contract_date', value)}
                />

                <ReadonlyFormGroup
                    label={window.__js_contracts.start_date}
                    value={this.state.model.start_date}
                />

                <DatepickerFormGroup
                    label={window.__js_contracts.end_date}
                    name="end_date_formatted"
                    value={this.state.model.end_date}
                    onChange={(value) => this.onChange('end_date', value)}
                />

                <DropdownFormGroup
                    label={window.__js_contracts.payment_term_field}
                    name="payment_term"
                    value={this.state.model.payment_term}
                    onChange={(value) => this.onChange('payment_term', value)}
                    options={paymentTerms}
                />

                <TextareaFormGroup
                    label={window.__js_contracts.notes_field}
                    name="notes"
                    value={this.state.model.notes}
                    onChange={(value) => this.onChange('notes', value)}
                />

                <TextFormGroup
                    label={window.__js_contracts.protocol_number}
                    name="hospital_protocol_number"
                    value={this.state.model.hospital_protocol_number}
                    onChange={(value) => this.onChange('hospital_protocol_number', value)}
                />

                {
                    this.props.accountHasContractAmendmentApproval
                        ? <DropdownFormGroup
                            label={window.__js_bid_reviews.select_reviewers_title}
                            name="reviewer_ids[]"
                            id="reviewer_ids"
                            isMulti={true}
                            value={this.state.model.reviewer_ids}
                            onChange={(value) => this.onChange('reviewer_ids', value)}
                            options={reviewers}
                        />
                        : null
                }
            </form>
        );
    }
}

const element = document.getElementById('create-amendment-form');

if (element) {
    const accountHasContractAmendmentApproval = (element.getAttribute('data-account-has-contract-amendment-approval') === '1');
    const submitUrl = element.getAttribute('data-submit-url');

    ReactDOM.render(
        <CreateAmendmentForm
            accountHasContractAmendmentApproval={accountHasContractAmendmentApproval}
            submitUrl={submitUrl}
        />,
        element
    );
}